import { api, type QueryParams } from './client';
import type { BulkAction, BulkResult, Paginated } from './types';

/**
 * Generic admin resource client (plan §3.3). Pages, families, and the 7 content collections
 * all expose the same publish/unpublish/CRUD surface, so one factory serves them. Per-route
 * granular permissions are enforced server-side (spec §9.0 note); the UI gates buttons.
 */
export function makeResource<T>(basePath: string) {
  return {
    async list(params?: QueryParams): Promise<Paginated<T>> {
      const res = await api.getRaw<T[]>(basePath, { locale: 'all', ...params });
      // Server sends `data: null` for an empty list; normalize to [] so the
      // Paginated<T> contract (data: T[]) holds for every consumer.
      return { data: res.data ?? [], meta: res.meta as Paginated<T>['meta'] };
    },
    get(id: string): Promise<T> {
      return api.get<T>(`${basePath}/${id}`, { locale: 'all' });
    },
    create(body: unknown): Promise<T> {
      return api.post<T>(basePath, body);
    },
    update(id: string, body: unknown): Promise<T> {
      return api.patch<T>(`${basePath}/${id}`, body);
    },
    remove(id: string): Promise<null> {
      return api.del(`${basePath}/${id}`);
    },
    publish(id: string, scheduledAt?: string): Promise<T> {
      return api.post<T>(`${basePath}/${id}/publish`, scheduledAt ? { scheduled_at: scheduledAt } : undefined);
    },
    unpublish(id: string): Promise<T> {
      return api.post<T>(`${basePath}/${id}/unpublish`);
    },
    bulk(action: BulkAction, ids: string[]): Promise<BulkResult> {
      return api.post<BulkResult>(`${basePath}/bulk`, { action, ids });
    },
  };
}

export type Resource<T> = ReturnType<typeof makeResource<T>>;
